import React, { createContext, useContext, useState, useEffect } from 'react';
import i18nService, { t, formatNumber, formatDate, formatCurrency, formatRelativeTime, plural } from '../i18n';

// Create context
const LanguageContext = createContext(null);

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(i18nService.getCurrentLanguage());
  const [isRTL, setIsRTL] = useState(i18nService.isRTL());

  // Handle language changes
  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail;
      setCurrentLanguage(newLanguage);
      setIsRTL(i18nService.isRTL());

      // Update document direction and language
      document.documentElement.lang = newLanguage;
      document.documentElement.dir = i18nService.getTextDirection();
    };

    // Listen for language change events
    window.addEventListener('languageChanged', handleLanguageChange);

    // Set initial document properties
    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = i18nService.getTextDirection();

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [currentLanguage]);

  // Change language
  const changeLanguage = async (language) => {
    try {
      // Load language if not already loaded
      await i18nService.loadLanguage(language);
      i18nService.setLanguage(language);
      setCurrentLanguage(language);
      setIsRTL(i18nService.isRTL());
    } catch (error) {
      // console.error('Failed to change language:', error);
      throw error;
    }
  };

  // Get available languages
  const getAvailableLanguages = () => {
    return i18nService.getAvailableLanguages();
  };

  // Translation function with context
  const translate = (key, params = {}) => {
    return i18nService.translate(key, params);
  };

  // Context value
  const value = {
    // State
    currentLanguage,
    isRTL,
    textDirection: i18nService.getTextDirection(),

    // Actions
    changeLanguage,
    getAvailableLanguages,

    // Translation functions
    t: translate,
    translate,
    formatNumber,
    formatDate,
    formatCurrency,
    formatRelativeTime,
    plural,

    // Utility functions
    isRTL: () => i18nService.isRTL(),
    getTextDirection: () => i18nService.getTextDirection(),
    getLocale: () => i18nService.getLocale(),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Higher-order component for class components
export const withLanguage = (Component) => {
  return function LanguageComponent(props) {
    const languageProps = useLanguage();
    return <Component {...props} {...languageProps} />;
  };
};

export default LanguageContext;